/**
 * DeployRolloutModal — "manage rollout to verticals" action for meta-broker
 * catalogue tiles (brief-17-admin-catalogue-tile-actions).
 *
 * Wraps the already-built promote-to-UAT pipeline (WP-ACP-2 / Brief 12,
 * scenario-promotion-allocation / ADR-068 §6): validate → allocate →
 * register-app-uat. This route runs on the meta-broker only (per the graph's
 * scenario-promotion-allocation note), so this modal is only ever rendered
 * from the meta-broker's own catalogue (mode 'tier-1-hub').
 *
 * Reuses the promoteServiceToUat() hook already used by SandboxProvisioningPage's
 * PromoteModal — no new backend surface, just a tile-level entry point into an
 * existing, working flow.
 */

import { useState, type MouseEvent } from 'react';
import { t } from '../i18n';
import type { TileConfig } from '../config/schema';
import { useAuth } from '../auth/KeycloakProvider';
import { promoteServiceToUat } from '../hooks/useProvisioning';

interface DeployRolloutModalProps {
  tile: TileConfig;
  onClose: () => void;
}

type Phase = 'confirm' | 'submitting' | 'done' | 'error';

export default function DeployRolloutModal({ tile, onClose }: DeployRolloutModalProps) {
  const { token } = useAuth();
  const [verticalBrokerId, setVerticalBrokerId] = useState('deal-broker');
  const [phase, setPhase] = useState<Phase>('confirm');
  const [error, setError] = useState<string | null>(null);

  function handleBackdropClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget && phase !== 'submitting') onClose();
  }

  async function handleConfirm() {
    if (!token) return;
    setPhase('submitting');
    setError(null);
    try {
      await promoteServiceToUat(token, tile.id, verticalBrokerId);
      setPhase('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('error');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="deploy-rollout-title"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'var(--scrim)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--sp-8)',
      }}
    >
      <div style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border-default)',
        width: '100%', maxWidth: '480px',
        padding: 'var(--sp-8)',
        display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)',
        boxShadow: 'var(--shadow-2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p className="dp-eyebrow" style={{ margin: '0 0 var(--sp-1)' }}>{t('tile.deploy.eyebrow')}</p>
            <h2 id="deploy-rollout-title" style={{
              fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-h3)', fontWeight: 600,
              color: 'var(--fg-1)', margin: 0,
            }}>
              {tile.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={phase === 'submitting'}
            aria-label={t('serviceDetail.close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-3)', fontSize: '20px', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {(phase === 'confirm' || phase === 'error') && (
          <>
            <div style={{
              border: '1px solid var(--border-default)', background: 'var(--bg-recessed)',
              borderRadius: 'var(--radius-md)', padding: 'var(--sp-4)',
              fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-2)',
            }}>
              {t('tile.deploy.explainer')}
            </div>

            <div>
              <label htmlFor="dp-deploy-vertical" style={{
                display: 'block', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
                fontWeight: 600, color: 'var(--fg-1)', marginBottom: 'var(--sp-1)',
              }}>
                {t('tile.deploy.targetLabel')}
              </label>
              <input
                id="dp-deploy-vertical"
                type="text"
                value={verticalBrokerId}
                onChange={(e) => setVerticalBrokerId(e.target.value)}
                style={{
                  width: '100%', padding: '8px var(--sp-3)',
                  borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border-default)',
                  borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)',
                  background: 'var(--bg-surface)', color: 'var(--fg-1)',
                }}
              />
            </div>

            {phase === 'error' && error && (
              <p style={{ color: 'var(--dp-crimson-600)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', margin: 0 }}>
                {error}
              </p>
            )}

            <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end' }}>
              <button className="dp-btn dp-btn--ghost" onClick={onClose}>{t('destructive.cancel')}</button>
              <button className="dp-btn dp-btn--primary" onClick={handleConfirm}>{t('tile.deploy.confirm')}</button>
            </div>
          </>
        )}

        {phase === 'submitting' && (
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-3)' }}>
            {t('tile.deploy.submitting')}
          </p>
        )}

        {phase === 'done' && (
          <>
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-2)' }}>
              {t('tile.deploy.done')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="dp-btn dp-btn--primary" onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
